import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle, AlertCircle, Eye, EyeOff, Lock } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { apiRequest } from '../lib/queryClient';

export default function PasswordResetComplete() {
  const [, setLocation] = useLocation();
  const [token, setToken] = useState<string | null>(null);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);

  // Password strength validation
  const passwordStrength = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password)
  };

  const isPasswordValid = Object.values(passwordStrength).every(Boolean);
  const passwordsMatch = password === confirmPassword && password.length > 0;

  // Get token from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      verifyToken(tokenFromUrl);
    } else {
      setLocation('/password-reset-request');
    }
  }, []);

  const verifyTokenMutation = useMutation({
    mutationFn: async (token: string) => {
      const response = await apiRequest('POST', '/api/password-reset/verify', { token });
      return response.json();
    },
    onSuccess: (data) => {
      setIsVerified(true);
      setUserInfo(data);
    },
    onError: () => {
      setLocation('/password-reset-request');
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async ({ token, password, confirmPassword }: { token: string; password: string; confirmPassword: string }) => {
      const response = await apiRequest('POST', '/api/password-reset/reset', { 
        token, 
        password, 
        confirmPassword 
      });
      return response.json();
    },
    onSuccess: () => {
      // Redirect to login with success message
      setLocation('/login?reset=success');
    },
    onError: (error: any) => {
      console.error('Password reset error:', error);
    }
  });

  const verifyToken = (tokenToVerify: string) => {
    verifyTokenMutation.mutate(tokenToVerify);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isPasswordValid || !passwordsMatch || !token) {
      return;
    }

    resetPasswordMutation.mutate({ token, password, confirmPassword });
  };

  if (verifyTokenMutation.isPending) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800 border-slate-700">
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-slate-300">Verifying reset link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isVerified) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800 border-slate-700">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-200 mb-2">Invalid Reset Link</h2>
            <p className="text-slate-400 mb-6">
              This password reset link is invalid or has expired.
            </p>
            <Button onClick={() => setLocation('/password-reset-request')} className="w-full">
              Request New Reset Link
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800 border-slate-700">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center">
              <Lock className="h-8 w-8 text-slate-900" />
            </div>
          </div>
          <CardTitle className="text-2xl text-green-500">Create New Password</CardTitle>
          <CardDescription className="text-slate-400">
            Hello {userInfo?.firstName}, please create a new secure password for your account
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 pr-10"
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              
              {/* Password strength indicator */}
              <div className="space-y-1 text-xs">
                <div className={`flex items-center gap-2 ${passwordStrength.length ? 'text-green-400' : 'text-slate-500'}`}>
                  <CheckCircle className="h-3 w-3" />
                  At least 8 characters
                </div>
                <div className={`flex items-center gap-2 ${passwordStrength.uppercase ? 'text-green-400' : 'text-slate-500'}`}>
                  <CheckCircle className="h-3 w-3" />
                  One uppercase letter
                </div>
                <div className={`flex items-center gap-2 ${passwordStrength.lowercase ? 'text-green-400' : 'text-slate-500'}`}>
                  <CheckCircle className="h-3 w-3" />
                  One lowercase letter
                </div>
                <div className={`flex items-center gap-2 ${passwordStrength.number ? 'text-green-400' : 'text-slate-500'}`}>
                  <CheckCircle className="h-3 w-3" />
                  One number
                </div>
                <div className={`flex items-center gap-2 ${passwordStrength.special ? 'text-green-400' : 'text-slate-500'}`}>
                  <CheckCircle className="h-3 w-3" />
                  One special character
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-300">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-300"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              
              {confirmPassword.length > 0 && !passwordsMatch && (
                <p className="text-red-400 text-xs">Passwords do not match</p>
              )}
              
              {confirmPassword.length > 0 && passwordsMatch && (
                <p className="text-green-400 text-xs">Passwords match</p>
              )}
            </div>

            {resetPasswordMutation.error && (
              <Alert className="bg-red-500/10 border-red-500/20">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-red-400">
                  {resetPasswordMutation.error.message || 'Failed to reset password. Please try again.'}
                </AlertDescription>
              </Alert>
            )}

            <Button 
              type="submit" 
              className="w-full bg-green-500 hover:bg-green-600 text-slate-900"
              disabled={!isPasswordValid || !passwordsMatch || resetPasswordMutation.isPending}
            >
              {resetPasswordMutation.isPending ? 'Resetting Password...' : 'Reset Password'}
            </Button>
          </form>

          <div className="mt-6 text-xs text-slate-500 text-center space-y-1">
            <p>• Your new password must meet all security requirements</p>
            <p>• This reset link can only be used once</p>
            <p>• You'll be redirected to login after successful reset</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
